import React, { useEffect, useCallback, useState, useRef } from 'react';
import { Search, Button, Loader } from '@vibe/core';
import { X, Filter, ChevronRight, ChevronDown, HelpCircle, Info } from 'lucide-react';
import { useItemSearch } from '../../hooks/useItemSearch';
import { useItemSearchStore, type BoardItem } from '../../stores/itemSearchStore';
import { useMonday } from '../../hooks/useMonday';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { format, parseISO } from 'date-fns';

// ─── Props ────────────────────────────────────────────────────────────────────

interface ItemSearchPanelProps {
  boardId: number;
  /** When multiple boards are configured, fetch items from all of them */
  multiBoardIds?: number[];
  /** Board name options for the board filter dropdown */
  multiBoardOptions?: Array<{ id: number; name: string }>;
  onClose: () => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

const ItemSearchPanel: React.FC<ItemSearchPanelProps> = ({
  boardId,
  multiBoardIds,
  multiBoardOptions,
  onClose,
}) => {
  const {
    filteredItems,
    filterOptions,
    cursor,
    isLoadingMore,
    fetchBoardItems,
    fetchMultipleBoardItems,
    loadFilterOptions,
    loadMore,
  } = useItemSearch();

  const {
    isLoading,
    error,
    filters,
    autoSaveFilter,
    setSearchQuery,
    setAssigneeFilter,
    setStatusFilter,
    setGroupFilter,
    setBoardFilter,
    setPlannedFilter,
    setAutoSaveFilter,
    clearFilters,
  } = useItemSearchStore();

  const isMultiBoard = multiBoardIds && multiBoardIds.length > 1;

  const { monday } = useMonday();

  // Debounce the search query (300ms) — server-side search triggers after 2+ chars
  const debouncedSearch = useDebouncedValue(filters.searchQuery, 300);

  // Track whether initial load has happened to avoid double-fetch on mount
  const initialLoadDone = useRef(false);

  // Helper to trigger a fetch with current filters
  const doFetch = useCallback(
    (searchText?: string) => {
      const activeFilters = filters;
      if (isMultiBoard) {
        // When a specific board is selected, use single-board fetch (supports cursor/load more)
        if (filters.boardIds.length === 1) {
          fetchBoardItems(filters.boardIds[0], searchText, activeFilters);
        } else {
          fetchMultipleBoardItems(multiBoardIds, searchText, activeFilters);
        }
      } else {
        fetchBoardItems(boardId, searchText, activeFilters);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [boardId, multiBoardIds, isMultiBoard, filters, fetchBoardItems, fetchMultipleBoardItems],
  );

  // The effective board for group/status filter options:
  // single board → that board; multi-board with board filter → selected board; else → undefined
  const effectiveBoardId = isMultiBoard
    ? (filters.boardIds.length === 1 ? filters.boardIds[0] : undefined)
    : boardId;

  // Initial load on mount
  useEffect(() => {
    initialLoadDone.current = false;
    doFetch();
    // Load filter options from API (users always, groups/statuses when single board)
    loadFilterOptions(isMultiBoard ? undefined : boardId);
    // Mark initial load as done after a tick so the debounced search effect doesn't re-fetch
    const timer = setTimeout(() => { initialLoadDone.current = true; }, 0);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boardId, multiBoardIds, isMultiBoard]);

  // Reload group/status filter options when the effective board changes (multi-board board filter)
  // Also clear status/group filters when no single board is selected (they don't apply cross-board)
  useEffect(() => {
    if (!initialLoadDone.current) return;
    if (!isMultiBoard) return; // single-board already loaded on mount
    loadFilterOptions(effectiveBoardId);
    if (effectiveBoardId == null) {
      // No single board selected — clear board-specific filters
      if (filters.statuses.length > 0) setStatusFilter([]);
      if (filters.groupIds.length > 0) setGroupFilter([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveBoardId]);

  // Server-side search when debounced query changes (2+ chars or cleared)
  useEffect(() => {
    if (!initialLoadDone.current) return;

    const searchText = debouncedSearch.length >= 2 ? debouncedSearch : undefined;
    doFetch(searchText);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  // Re-fetch from API when filters change (assignee, status, group, board)
  // These filters are sent server-side now, not just applied client-side
  useEffect(() => {
    if (!initialLoadDone.current) return;

    const searchText = debouncedSearch.length >= 2 ? debouncedSearch : undefined;
    doFetch(searchText);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.assigneeIds, filters.statuses, filters.groupIds, filters.boardIds]);

  // Load saved filters from localStorage
  useEffect(() => {
    const stored = localStorage.getItem(`rp-item-search-filters-${boardId}`);
    if (stored) {
      try {
        const parsed = JSON.parse(stored) as {
          autoSave?: boolean;
          filters?: typeof filters;
        };
        if (parsed.autoSave) {
          setAutoSaveFilter(true);
          if (parsed.filters) {
            if (parsed.filters.searchQuery) setSearchQuery(parsed.filters.searchQuery);
            if (parsed.filters.assigneeIds?.length) setAssigneeFilter(parsed.filters.assigneeIds);
            if (parsed.filters.statuses?.length) setStatusFilter(parsed.filters.statuses);
            if (parsed.filters.groupIds?.length) setGroupFilter(parsed.filters.groupIds);
            if (parsed.filters.plannedFilter) setPlannedFilter(parsed.filters.plannedFilter);
          }
        }
      } catch { /* ignore invalid stored data */ }
    }
  }, [boardId, setAutoSaveFilter, setSearchQuery, setAssigneeFilter, setStatusFilter, setGroupFilter, setPlannedFilter]);

  // Persist filters when autoSaveFilter is enabled
  useEffect(() => {
    if (autoSaveFilter) {
      localStorage.setItem(
        `rp-item-search-filters-${boardId}`,
        JSON.stringify({ autoSave: true, filters }),
      );
    }
  }, [autoSaveFilter, filters, boardId]);

  const handleSearchChange = useCallback(
    (value: string) => setSearchQuery(value),
    [setSearchQuery],
  );

  const handleSearchClear = useCallback(
    () => setSearchQuery(''),
    [setSearchQuery],
  );

  const handleClearFilters = useCallback(() => {
    clearFilters();
    localStorage.removeItem(`rp-item-search-filters-${boardId}`);
  }, [clearFilters, boardId]);

  const handleAutoSaveToggle = useCallback(() => {
    const next = !autoSaveFilter;
    setAutoSaveFilter(next);
    if (!next) {
      localStorage.removeItem(`rp-item-search-filters-${boardId}`);
    }
  }, [autoSaveFilter, setAutoSaveFilter, boardId]);

  const handleOpenItem = useCallback(
    (mondayItemId: number) => {
      monday.execute('openItemCard', { itemId: mondayItemId });
    },
    [monday],
  );

  // Track which parent items are expanded to show subitems
  const [expandedItems, setExpandedItems] = useState<Set<number>>(() => new Set());

  const toggleExpanded = useCallback((mondayItemId: number) => {
    setExpandedItems((prev) => {
      const next = new Set(prev);
      if (next.has(mondayItemId)) {
        next.delete(mondayItemId);
      } else {
        next.add(mondayItemId);
      }
      return next;
    });
  }, []);

  return (
    <div className="rp-item-search-panel">
      {/* Header */}
      <div className="rp-item-search-panel__header">
        <h3 className="rp-item-search-panel__title">Plan Item</h3>
        <div className="rp-item-search-panel__header-actions">
          <div className="rp-item-search-panel__help-wrapper">
            <button className="rp-item-search-panel__help-btn" aria-label="Help">
              <HelpCircle size={16} />
            </button>
            <div className="rp-item-search-panel__help-tooltip">
              <strong>How to use Plan Item</strong>
              <p>Search works on <strong>main items only</strong> — type a name to find items from your board, then drag them onto the scheduler to plan them.</p>
              <p>If a main item has subitems, click the expand arrow (&#9656;) to reveal them. You can then drag individual subitems onto the scheduler.</p>
              <p>Note: you cannot search for subitems directly — find the parent item first, then expand it.</p>
              <p>Use filters below to narrow results by assignee, status, group, or planned/unplanned state.</p>
            </div>
          </div>
          <button
            className="rp-item-search-panel__close"
            onClick={onClose}
            aria-label="Close panel"
          >
            <X size={16} />
          </button>
        </div>
      </div>

      {/* Search bar */}
      <div className="rp-item-search-panel__search">
        <div className="rp-item-search-panel__search-input">
          <Search
            placeholder="Type 2+ characters to search..."
            value={filters.searchQuery}
            onChange={handleSearchChange}
            onClear={handleSearchClear}
            size="small"
          />
        </div>
        <button className="rp-item-search-panel__close" aria-label="Filter">
          <Filter size={14} />
        </button>
      </div>

      {/* Filters */}
      <div className="rp-item-search-panel__filters">
        {/* Clear filters */}
        <div className="rp-item-search-panel__filter-actions">
          <div />
          <Button
            kind="tertiary"
            size="small"
            onClick={handleClearFilters}
          >
            Clear
          </Button>
        </div>

        {/* Board dropdown (multi-board mode only) */}
        {isMultiBoard && multiBoardOptions && (
          <div className="rp-item-search-panel__filter-row">
            <span className="rp-item-search-panel__filter-label">Board</span>
            <div className="rp-item-search-panel__filter-select">
              <select
                value={filters.boardIds.length === 1 ? String(filters.boardIds[0]) : ''}
                onChange={(e) => {
                  const val = e.target.value;
                  setBoardFilter(val ? [Number(val)] : []);
                }}
              >
                <option value="">All boards</option>
                {multiBoardOptions.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}

        {/* Assignee dropdown */}
        <div className="rp-item-search-panel__filter-row">
          <span className="rp-item-search-panel__filter-label">Assignee</span>
          <div className="rp-item-search-panel__filter-select">
            <select
              value={filters.assigneeIds.length === 1 ? String(filters.assigneeIds[0]) : ''}
              onChange={(e) => {
                const val = e.target.value;
                setAssigneeFilter(val ? [Number(val)] : []);
              }}
            >
              <option value="">Choose assignee ...</option>
              {filterOptions.assignees.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Status dropdown — only shown when a single board is in context */}
        {effectiveBoardId != null && (
          <div className="rp-item-search-panel__filter-row">
            <span className="rp-item-search-panel__filter-label">Status</span>
            <div className="rp-item-search-panel__filter-select">
              <select
                value={filters.statuses.length === 1 ? filters.statuses[0] : ''}
                onChange={(e) => {
                  const val = e.target.value;
                  setStatusFilter(val ? [val] : []);
                }}
              >
                <option value="">Choose status ...</option>
                {filterOptions.statuses.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}

        {/* Group dropdown — only shown when a single board is in context */}
        {effectiveBoardId != null && (
          <div className="rp-item-search-panel__filter-row">
            <span className="rp-item-search-panel__filter-label">Group</span>
            <div className="rp-item-search-panel__filter-select">
              <select
                value={filters.groupIds.length === 1 ? filters.groupIds[0] : ''}
                onChange={(e) => {
                  const val = e.target.value;
                  setGroupFilter(val ? [val] : []);
                }}
              >
                <option value="">Choose group ...</option>
                {filterOptions.groups.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}

      </div>

      {/* Item list */}
      <div className="rp-item-search-panel__list">
        {isLoading && (
          <div className="rp-item-search-panel__loading">
            <Loader size="small" />
          </div>
        )}

        {error && (
          <div className="rp-item-search-panel__error">
            Error: {error}
          </div>
        )}

        {!isLoading && !error && filteredItems.length === 0 && (
          <div className="rp-item-search-panel__empty">
            {filters.searchQuery || filters.assigneeIds.length || filters.statuses.length || filters.groupIds.length || filters.plannedFilter !== 'all'
              ? 'No items match your filters.'
              : 'No items found on this board.'}
          </div>
        )}

        {!isLoading &&
          filteredItems.map((item) => {
            const hasSubitems = item.subitems && item.subitems.length > 0;
            const isExpanded = expandedItems.has(item.mondayItemId);

            return (
              <div key={item.mondayItemId} className="rp-item-search-panel__item-group">
                <div className="rp-item-search-panel__item-row">
                  {hasSubitems && (
                    <button
                      className="rp-item-search-panel__expand-toggle"
                      onClick={() => toggleExpanded(item.mondayItemId)}
                      aria-label={isExpanded ? 'Collapse subitems' : 'Expand subitems'}
                    >
                      {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    </button>
                  )}
                  <DraggableSearchItem
                    item={item}
                    onOpenItem={handleOpenItem}
                    subitemCount={hasSubitems ? item.subitems!.length : undefined}
                    hasExpandToggle={!!hasSubitems}
                  />
                </div>
                {hasSubitems && isExpanded && (
                  <div className="rp-item-search-panel__subitems">
                    {item.subitems!.map((sub) => (
                      <DraggableSearchItem
                        key={sub.mondayItemId}
                        item={sub}
                        onOpenItem={handleOpenItem}
                        isSubitem
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })}

        {/* Load More button */}
        {!isLoading && cursor && (
          <div className="rp-item-search-panel__load-more">
            <Button
              kind="tertiary"
              size="small"
              loading={isLoadingMore}
              onClick={() => loadMore(effectiveBoardId ?? boardId)}
            >
              Load More
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Draggable Item ───────────────────────────────────────────────────────────

interface DraggableSearchItemProps {
  item: BoardItem;
  onOpenItem: (mondayItemId: number) => void;
  isSubitem?: boolean;
  subitemCount?: number;
  /** Whether the parent row has an expand toggle (affects left margin) */
  hasExpandToggle?: boolean;
}

const DEFAULT_ITEM_COLOR = '#579bfc';

const DraggableSearchItem: React.FC<DraggableSearchItemProps> = ({
  item,
  onOpenItem,
  isSubitem,
  subitemCount,
  hasExpandToggle,
}) => {
  const dateLabel = item.startDate
    ? format(parseISO(item.startDate), 'MMM d')
    : undefined;

  const bgColor = item.groupColor || DEFAULT_ITEM_COLOR;

  const handleDragStart = (e: React.DragEvent) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData(
      'application/rp-item',
      JSON.stringify({ mondayItemId: item.mondayItemId, name: item.name }),
    );
  };

  const cardClass = [
    'rp-item-search-panel__item-card',
    isSubitem && 'rp-item-search-panel__item-card--subitem',
    !hasExpandToggle && !isSubitem && 'rp-item-search-panel__item-card--no-toggle',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className={cardClass}
      style={{ backgroundColor: bgColor }}
      draggable
      onDragStart={handleDragStart}
      onClick={() => onOpenItem(item.mondayItemId)}
    >
      <div className="rp-item-search-panel__item-card-top">
        {item.status && (
          <span className="rp-item-search-panel__item-card-status">
            {item.status}
          </span>
        )}
        {dateLabel && (
          <span className="rp-item-search-panel__item-card-date">
            {dateLabel}
          </span>
        )}
        {subitemCount != null && subitemCount > 0 && (
          <span className="rp-item-search-panel__subitem-count">
            {subitemCount}
          </span>
        )}
      </div>
      <span className="rp-item-search-panel__item-card-name">{item.name}</span>
    </div>
  );
};

export default ItemSearchPanel;
